import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  Grid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import api from "api/api";
import { useAttendanceContext } from "contexts/attendance-context";
import { openModalConfirm } from "components/modal-confirm-new";
import { InputSearch } from "components/input-search";
import { Toast } from "components/toast";

interface EnabledTag {
  id: number;
  nome: string;
  ativo: boolean;
  systemTag: boolean;
  dataCriacao: string;
  dataAtualizacao: string;
}

interface AssignedTag {
  tagAtendimentoId: number;
  tagId: number;
  tagNome: string;
  atendimentoId: number;
}

export function TagsSelection() {
  const { formControl } = useAttendanceContext();
  const formValues = formControl.values;
  const attendanceId = formValues.id;

  const [enabledTags, setEnabledTags] = useState<EnabledTag[]>([]);
  const [assignedTags, setAssignedTags] = useState<AssignedTag[]>([]);
  const [showInputSearch, setShowInputSearch] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);
  const [isLoadingAdding, setIsLoadingAdding] = useState(false);

  const enabledTagsOptions = enabledTags
    .map((tag) => ({
      value: tag.id,
      name: tag.nome,
    }))
    .filter(
      ({ value: id }) =>
        !assignedTags.some((assignedTag) => assignedTag.tagId === id)
    );

  // Fetch enabled tags
  const findTagsEnabled = async () => {
    setIsLoadingAdding(true);
    try {
      const { data } = await api.get("/tags/enabled");
      setEnabledTags(data);
    } catch (e: any) {
      console.log("erro ao buscar tags enabled: ", e);
      const errorMessage =
        e.response?.data?.msgInterface ||
        "Ocorreu um erro ao tentar buscar tags ativas. Tente novamente.";
      Toast({
        status: "error",
        title: "Erro ao buscar tag ativas",
        description: errorMessage,
      });
    } finally {
      setIsLoadingAdding(false);
    }
  }

  const findAllTagsAssigned = async () => {
    setIsLoadingAssigned(true);
    try {
      const { data } = await api
        .get(`/tags/find-all-assigned/${attendanceId}/tag-atendimento`);
      setAssignedTags(data);
    } catch (e: any) {
      console.log("erro ao buscar tags assigned: ", e);
      const errorMessage =
        e.response?.data?.msgInterface ||
        "Ocorreu um erro ao tentar buscar tags atribuidas. Tente novamente.";
      Toast({
        status: "error",
        title: "Erro ao buscar tag atribuidas",
        description: errorMessage,
      });
    } finally {
      setIsLoadingAssigned(false);
    }
  }

  useEffect(() => {
    findTagsEnabled();
    if (attendanceId) {
      findAllTagsAssigned();
    }
  }, [attendanceId]);

  const handleAdd = async () => {
    if (!selectedTagId || !attendanceId) return;

    setIsAdding(true);
    try {
      await api.post("/tags/insert/tag-atendimento", {
        tagId: selectedTagId,
        atendimentoId: attendanceId,
      });

      // Refresh assigned tags
      const res = await api.get(
        `/tags/find-all-assigned/${attendanceId}/tag-atendimento`
      );
      setAssignedTags(res.data);
      setShowInputSearch(false);
      setSelectedTagId(null);
    } catch (error: any) {
      console.error("Error adding tag:", error);
      const errorMessage =
        error.response?.data?.msgInterface ||
        "Ocorreu um erro ao tentar adicionar a tag. Tente novamente.";

      Toast({
        status: "error",
        title: "Erro ao adicionar tag",
        description: errorMessage,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = (tagId: number) => {
    openModalConfirm({
      message: "Tem certeza que deseja remover esta tag?",
      onConfirm: async () => {
        try {
          await api.delete(
            `/tags/${tagId}/delete/${attendanceId}/tag-atendimento`
          );

          // Refresh assigned tags
          const res = await api.get(
            `/tags/find-all-assigned/${attendanceId}/tag-atendimento`
          );
          setAssignedTags(res.data);
        } catch (error) {
          console.error("Error deleting tag:", error);
        }
      },
    });
  };

  return (
    <Box p="25px" borderBottom="1px solid #E8EAED">
      <Flex justifyContent="space-between">
        <Text fontWeight="medium" fontSize="16px">
          Tags
        </Text>
        {!showInputSearch && (
          <Button
            leftIcon={<AddIcon w="12px" h="12px" />}
            onClick={() => setShowInputSearch(true)}
            size="sm"
            colorScheme="blue"
            variant="link"
            isLoading={isLoadingAdding}
            loadingText="Carregando"
          >
            Adicionar
          </Button>
        )}
      </Flex>

      {/* Assigned Tags */}
      {!isLoadingAssigned ? (
        <Box>
          {!!assignedTags.length && (
            <Flex mt="10px" wrap="wrap" gap="10px">
              {assignedTags.map((tag) => (
                <Badge
                  key={tag.tagAtendimentoId}
                  colorScheme="blue"
                  display="flex"
                  alignItems="center"
                  gap="5px"
                  px="10px"
                  py="5px"
                  borderRadius="md"
                >
                  {tag.tagNome}
                  <CloseButton
                    size="sm"
                    onClick={() => handleDelete(tag.tagId)}
                  />
                </Badge>
              ))}
            </Flex>
          )}
        </Box>
      ) : (<><Spinner /></>)}

      {/* Add Tag Section */}
      {showInputSearch && (
        <Grid
          templateColumns="auto 114px 114px"
          mt="20px"
          alignItems="center"
          gap="10px"
        >
          <InputSearch
            options={enabledTagsOptions}
            onChange={(selectedTagId) => setSelectedTagId(selectedTagId)}
            checkIsInclude={(tag) => tag.value === selectedTagId} // Not used for single select
            inputProps={{ placeholder: "Selecione uma tag" }}
            value={
              enabledTagsOptions.find((tag) => tag.value === selectedTagId)
                ?.name
            }
            closeOnSelect={true}
            isDisabled={isAdding}
            openOnFocus
          />

          <Button
            leftIcon={<AddIcon w="12px" h="12px" />}
            onClick={handleAdd}
            size="sm"
            colorScheme="blue"
            isDisabled={!selectedTagId}
            isLoading={isAdding}
          >
            Adicionar
          </Button>
          <Button
            leftIcon={<CloseIcon w="12px" h="12px" />}
            variant="outline"
            onClick={() => setShowInputSearch(false)}
            size="sm"
            isLoading={isAdding}
          >
            Fechar
          </Button>
        </Grid>
      )}
    </Box>
  );
}
